import { useMemo, useState, useCallback, type FC } from "react";
import { useDevices } from "../stores/device_store";
import { useDeviceGroups } from "../stores/device_group_store";
import { useCertificates } from "../stores/certificate_store";
import { useCertificatePolicies } from "../stores/certificate_policy_store";
import { useSearchParams } from "react-router-dom";
import { Pagination } from "../components/pagination";
import { useCustomerId } from "../stores/customer_id_store";
import { UnassignedPlaceholder } from "../components/unassigned_placeholder";
import type { Device, DeviceStatus } from "../../../shared/models/device";
import type {
  Certificate,
  CertificateStatus,
} from "../../../shared/models/certificate";

const STATUS_LABELS: Record<DeviceStatus, string> = {
  active: "Active",
  revoked: "Revoked",
  cloned: "Cloned",
};

const STATUS_BADGE_CLASS: Record<DeviceStatus, string> = {
  active: "vmx-badge-success",
  revoked: "vmx-badge-danger",
  cloned: "vmx-badge-warning",
};

const CERT_STATUS_BADGE_CLASS: Record<CertificateStatus, string> = {
  active: "vmx-badge-success",
  expiring: "vmx-badge-warning",
  expired: "vmx-badge-neutral",
  revoked: "vmx-badge-danger",
};

function getLatestCertificate(
  deviceId: string,
  certificates: Certificate[]
): Certificate | undefined {
  const deviceCerts = certificates.filter((c) => c.deviceId === deviceId);
  if (deviceCerts.length === 0) return undefined;
  return deviceCerts.sort(
    (a, b) => new Date(b.validFrom).getTime() - new Date(a.validFrom).getTime()
  )[0];
}

const DeviceDetailsModal: FC<{
  device: Device;
  certificates: Certificate[];
  groupName: string;
  policyName: string;
  onClose: () => void;
}> = ({ device, certificates, groupName, policyName, onClose }) => {
  const latestCert = getLatestCertificate(device.id, certificates);

  return (
    <div className="vmx-modal-backdrop" onClick={onClose}>
      <div className="vmx-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vmx-modal-header">
          <h2 className="vmx-modal-title">Device Details</h2>
          <button className="vmx-modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="vmx-modal-body">
          <table className="vmx-detail-table">
            <tbody>
              <tr>
                <th>Device ID</th>
                <td>{device.id}</td>
              </tr>
              <tr>
                <th>Device Group ID</th>
                <td>{groupName}</td>
              </tr>
              <tr>
                <th>Certificate Status</th>
                <td>
                  {latestCert ? (
                    <span
                      className={`vmx-badge ${CERT_STATUS_BADGE_CLASS[latestCert.status]}`}
                    >
                      {latestCert.status}
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
              <tr>
                <th>Certificate Policy</th>
                <td>{policyName}</td>
              </tr>
              <tr>
                <th>Certificate Valid From</th>
                <td>
                  {latestCert
                    ? new Date(latestCert.validFrom).toLocaleString()
                    : "—"}
                </td>
              </tr>
              <tr>
                <th>Certificate Valid To</th>
                <td>
                  {latestCert
                    ? new Date(latestCert.validTo).toLocaleString()
                    : "—"}
                </td>
              </tr>
              <tr>
                <th>Last Certificate Issued Date</th>
                <td>
                  {latestCert
                    ? new Date(latestCert.validFrom).toLocaleString()
                    : "—"}
                </td>
              </tr>
              <tr>
                <th>Last Seen Timestamp</th>
                <td>
                  {device.lastSeenAt
                    ? new Date(device.lastSeenAt).toLocaleString()
                    : "—"}
                </td>
              </tr>
              <tr>
                <th>Device Status</th>
                <td>
                  <span
                    className={`vmx-badge ${STATUS_BADGE_CLASS[device.status]}`}
                  >
                    {STATUS_LABELS[device.status]}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="vmx-modal-footer">
          <button className="vmx-btn vmx-btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export const Devices: FC = () => {
  const customerId = useCustomerId();
  const devices = useDevices();
  const groups = useDeviceGroups();
  const certificates = useCertificates();
  const policies = useCertificatePolicies();
  const [searchParams] = useSearchParams();
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);

  const groupFilter = searchParams.get("groupId");

  const filteredDevices = useMemo(() => {
    let result = [...devices];
    if (groupFilter) {
      result = result.filter((d) => d.deviceGroupId === groupFilter);
    }
    return result.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [devices, groupFilter]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const totalItems = filteredDevices.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedDevices = filteredDevices.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize
  );

  const getGroupName = useCallback(
    (groupId: string) => {
      const group = groups.find((g) => g.id === groupId);
      return group?.name ?? "—";
    },
    [groups]
  );

  const getPolicyNameForDevice = useCallback(
    (deviceId: string) => {
      const latestCert = getLatestCertificate(deviceId, certificates);
      if (!latestCert) return "—";
      const policy = policies.find(
        (p) => p.id === latestCert.certificatePolicyId
      );
      return policy?.name ?? "—";
    },
    [certificates, policies]
  );

  if (!customerId) {
    return <UnassignedPlaceholder />;
  }

  return (
    <div className="vmx-page">
      <div className="vmx-page-header">
        <div>
          <div className="vmx-page-title">📱 Devices</div>
          <div className="vmx-page-subtitle">
            View and monitor registered devices across your device groups.
          </div>
        </div>
      </div>

      {filteredDevices.length === 0 ? (
        <div className="vmx-card">
          <div className="vmx-empty">
            <div className="vmx-empty-icon">📱</div>
            <div className="vmx-empty-title">No devices yet</div>
            <div className="vmx-empty-text">
              Devices will appear here once they register with the system.
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="vmx-table-wrapper vmx-card-table-wrapper">
            <table className="vmx-table vmx-card-table">
              <thead>
                <tr>
                  <th>Device ID</th>
                  <th>Device Group</th>
                  <th>Status</th>
                  <th>Last Seen</th>
                  <th>Registered</th>
                </tr>
              </thead>
              <tbody>
                {paginatedDevices.map((device) => (
                  <tr
                    key={device.id}
                    onClick={() => setSelectedDevice(device)}
                    style={{ cursor: "pointer" }}
                  >
                    <td
                      data-label="Device ID"
                      style={{
                        fontWeight: 600,
                        color: "var(--vmx-gray-800)",
                      }}
                    >
                      {device.id}
                    </td>
                    <td data-label="Device Group">
                      {getGroupName(device.deviceGroupId)}
                    </td>
                    <td data-label="Status">
                      <span
                        className={`vmx-badge ${STATUS_BADGE_CLASS[device.status]}`}
                      >
                        {STATUS_LABELS[device.status]}
                      </span>
                    </td>
                    <td data-label="Last Seen">
                      {device.lastSeenAt
                        ? new Date(device.lastSeenAt).toLocaleDateString()
                        : "—"}
                    </td>
                    <td data-label="Registered">
                      {new Date(device.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={safePage}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
          />
        </>
      )}

      {selectedDevice && (
        <DeviceDetailsModal
          device={selectedDevice}
          certificates={certificates}
          groupName={getGroupName(selectedDevice.deviceGroupId)}
          policyName={getPolicyNameForDevice(selectedDevice.id)}
          onClose={() => setSelectedDevice(null)}
        />
      )}
    </div>
  );
};
